import { FC, useEffect, useMemo, useState } from "react";
import BillingFormData from "./types/billing-form.data";
import billingDataRouteRequestsFactory from "./factory/billing-data-route-requests.factory";
import billingMapRoutesFactory from "./factory/billing-map-routes.factory";
import billingDataFactory from "./factory/billing-data.factory";
import BillingDetailsComponent from "./billing-details/billing-details.component";
import ContractDetailsComponent from "./contract-details/contract-details.component";
import MapRoute from "../../../../common/components/map/types/map-route";
import MapMarker from "../../../../common/components/map/types/map-marker";
import Row from "../../../../common/components/grid/row";
import Column from "../../../../common/components/grid/column";
import MapComponent from "../../../../common/components/map/map.component";
import ButtonComponent from "../../../../common/components/button/button.component";
import CardComponent from "../../../../common/components/card/card.component";
import SearchRoadRoutingResponse from "../../../../common/utils/search-road-route/search-road-routing.response";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import billingsUserPermissionsHelper from "../../common/user-permissions/billings-user-permission.helper";
import { useAppContext } from "../../../../context/app.context";
import { ActionOption } from "./types/action-option";
import BillingsTaxiTaxiModal from "./billings-taxi-taxi-modal";
import BillingStatus from "../../types/billing-status";
import billingStatusUpdateRequestFactory from "./factory/billing-status-update.factory";
import { useParams } from "react-router-dom";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import BillingTaxiTaxiDetailsRouteParams from "../../common/routes/types/billing-taxi-taxi-details-route-params";
import RelatedBillingsCargoTaxiComponent from "../common/related-billings/cargo-taxi/related-billings-cargo-taxi.component";
import RelatedBillingsTaxiDriverComponent from "../common/related-billings/taxi-driver/related-billings-taxi-driver.component";
import RelatedBillingsTaxiTaxiComponent from "../common/related-billings/taxi-taxi/related-billings-taxi-taxi.component";
import BillingsRouteDetailsOverviewComponent from "../../common/route-details/billings-route-details-overview.component";
import MessengerComponent from "../../../../common/components/messenger/messenger.component";
import billingsTaxiTaxiHelper from "./billings-taxi-taxi.helper";
import MessengerBillingType from "../../../../common/components/messenger/types/messenger-billing-type";
import billingsMapMarkersFactory from "../../common/map-markers/billings-map-markers.factory";
import TabsData from "../../../../common/components/tabs/common/types/tabs-data";
import BillingsToolsComponent from "../../common/tools/billings-tools.component";
import BillingRouteDetailsComponent from "./billing-route-details/billing-route-details.component";
import TabsComponent from "../../../../common/components/tabs/tabs.component";
import OrderDetailsHistoryComponent from "../../../order/details/history/order-details-history.component";
import BillingModel from "../../types/billing-model";
import searchRoadRoutingService from "../../../../common/utils/search-road-route/search-road-routing.service";
import useAbort from "../../../../common/hooks/use-abort";
import useBillingsTaxiTaxiDetails from "../../../../common/services/billings/taxi-taxi/details/use-billings-taxi-taxi-details";
import BillingsTaxiTaxiDetailsParams from "../../../../common/services/billings/taxi-taxi/details/billings-taxi-taxi-details-params";
import billingService from "../../../../common/services/billings/billings.service";
import useGpsPlanEntryRecords from "../../../../common/services/gps/plan-entry-records/use-gps-plan-entry-records";
import GpsPlanEntryRecordsParams from "../../../../common/services/gps/plan-entry-records/gps-plan-entry-records-params";

const BillingsTaxiTaxiViewComponent: FC = () => {
  const { billingUuid } = useParams<BillingTaxiTaxiDetailsRouteParams>();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const [billingData, setBillingData] = useState<BillingFormData>();
  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>();
  const [plannedMapRoute, setPlannedMapRoute] = useState<MapRoute | null>(null);

  const [routeId, setRouteId] = useState("");
  const [shouldRetrieveRouteData, setShouldRetrieveRouteData] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [actionOption, setActionOption] = useState<ActionOption>();
  const [planEntryUuid, setPlanEntryUuid] = useState("");
  const [shouldShowOrderHistory, setShouldShowOrderHistory] = useState(false);

  const billingsTaxiTaxiDetails = useBillingsTaxiTaxiDetails();
  const billingsTaxiTaxiDetailsAbort = useAbort();

  const billingsTaxiTaxiUpdateStatusAbort = useAbort();

  const gpsPlanEntryRecords = useGpsPlanEntryRecords();
  const gpsPlanEntryRecordsAbort = useAbort();

  const documentTitle = appTranslationsHelper
    .getDocumentTitleTranslations()
    .billingsTaxiWithTaxiView.replace("#{routeId}", routeId);

  useDocumentTitle(documentTitle);

  useEffect(() => {
    const breadcrumbs = billingBreadcrumbsHelper.getTaxiWithTaxiViewBreadcrumbs(
      {
        billingUuid: billingUuid!,
        routeId,
      },
    );

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, routeId]);

  useEffect(() => {
    fetchData();

    return billingsTaxiTaxiDetailsAbort.revoke;
  }, []);

  const fetchData = () => {
    if (!billingUuid) return;

    const params: BillingsTaxiTaxiDetailsParams = {
      billingUuid: billingUuid,
    };

    billingsTaxiTaxiDetails.load(params, billingsTaxiTaxiDetailsAbort.signal);
  };

  useEffect(() => {
    if (!planEntryUuid) {
      return;
    }

    const params: GpsPlanEntryRecordsParams = {
      planEntryUuid: planEntryUuid,
    };

    gpsPlanEntryRecords.load(params, gpsPlanEntryRecordsAbort.signal);

    return gpsPlanEntryRecordsAbort.revoke;
  }, [planEntryUuid]);

  useEffect(() => {
    if (!billingData || !shouldRetrieveRouteData) {
      return;
    }

    const mapMarkers = billingsMapMarkersFactory.createMapMarkers(
      billingData.billingNodes,
    );

    const routeRequests =
      billingDataRouteRequestsFactory.createBillingDataRouteRequests(
        billingData.billingNodes,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    routeRequests.forEach((routeRequest) => {
      const fetch = searchRoadRoutingService.search(routeRequest);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];
      const lastResponseIndex = responses.length - 1;
      let lastResponseDuration = 0;

      responses.forEach((response, index) => {
        const mapRoute = response.routes[0]
          ? billingMapRoutesFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }

        if (index == lastResponseIndex) {
          lastResponseDuration = response.routes[0].duration;
        }
      });

      const newMapRouteWaypoints: MapRoute["waypoints"] = [];

      mapRouteWaypointGroups.forEach((waypoint) => {
        newMapRouteWaypoints.push(...waypoint);
      });

      const newMapRoute: MapRoute = {
        waypoints: newMapRouteWaypoints,
        options: { color: "red" },
      };

      if (
        billingData.contractDetails &&
        billingData.contractDetails.model !== BillingModel.AB
      ) {
        const lastBillingNodeIndex = billingData.billingNodes.length - 1;
        const secondToLastBillingNodePlannedDate =
          billingData.billingNodes[lastBillingNodeIndex - 1].plannedDate;

        if (secondToLastBillingNodePlannedDate) {
          const lastBillingNodePlannedDate = new Date(
            new Date(secondToLastBillingNodePlannedDate).getTime() +
              1000 * lastResponseDuration,
          );

          billingData.billingNodes[lastBillingNodeIndex].plannedDate =
            lastBillingNodePlannedDate;
          billingData.billingNodes[lastBillingNodeIndex].checkoutDate =
            lastBillingNodePlannedDate;

          setBillingData(billingData);
        }
      }

      setPlannedMapRoute(newMapRoute);
      setMapMarkers(mapMarkers);
      setShouldRetrieveRouteData(false);
    });
  }, [shouldRetrieveRouteData]);

  useEffect(() => {
    if (!billingsTaxiTaxiDetails.data) return;

    const billingData = billingDataFactory.createBillingData(
      billingsTaxiTaxiDetails.data,
    );

    setBillingData(billingData);
    setPlanEntryUuid(billingsTaxiTaxiDetails.data.planEntryUuid);
    setShouldRetrieveRouteData(true);
    setRouteId(String(billingData.internalOrderId));
  }, [billingsTaxiTaxiDetails.data]);

  const completedMapRoute = useMemo(() => {
    if (!gpsPlanEntryRecords.data) return null;

    const gpsData: MapRoute = {
      waypoints: gpsPlanEntryRecords.data.data.map((x) => {
        return { latitude: x.latitude, longitude: x.longitude };
      }),
      options: { color: "#3699ff" },
    };

    return gpsData;
  }, [gpsPlanEntryRecords.data]);

  const mapRoutes: MapRoute[] = useMemo(() => {
    const finalMapRoutes: MapRoute[] = [];
    if (plannedMapRoute) {
      finalMapRoutes.push(plannedMapRoute);
    }
    if (completedMapRoute) {
      finalMapRoutes.push(completedMapRoute);
    }
    return finalMapRoutes;
  }, [plannedMapRoute, completedMapRoute]);

  const onSubmitHandle = (comment: string) => {
    if (!actionOption || !billingUuid) return;

    const request =
      billingStatusUpdateRequestFactory.createUpdateBillingStatusRequest(
        billingUuid,
        actionOption,
        comment,
      );

    billingService
      .updateTaxiTaxiStatusBillings(
        request,
        billingsTaxiTaxiUpdateStatusAbort.signal,
      )
      .then(fetchData);

    setIsModalVisible(false);
  };

  const onBillingStatusUpdate = (actionOption: ActionOption) => {
    setActionOption(actionOption);
    setIsModalVisible(true);
  };

  const onModalClose = () => {
    setIsModalVisible(false);
  };

  const { user } = useAppContext();

  const translations =
    billingsTranslationsHelper.getTaxiTaxiViewBillingsTranslations();

  const userPermissions = billingsUserPermissionsHelper.getPermissions(
    user?.roles!,
  );

  const isProducer = () => {
    return (
      billingData?.producerTaxiUuid ===
      user?.aspects.taxiOfficer?.taxiCorporationUuid
    );
  };

  const isConsumer = () => {
    return (
      billingData?.consumerTaxiUuid ===
      user?.aspects.taxiOfficer?.taxiCorporationUuid
    );
  };

  const messengerChannelsAvailability =
    billingsTaxiTaxiHelper.getMessengerChannelAvailability();

  const tabsData: TabsData = useMemo(() => {
    if (!billingData) {
      return [];
    }

    return billingDataFactory.createTabsData(
      billingData.billingContributions,
      billingsTaxiTaxiDetails.isLoading,
    );
  }, [billingData]);

  const onOrderHistoryButtonClick = () => {
    if (shouldShowOrderHistory) {
      setShouldShowOrderHistory(false);
      return;
    }

    setShouldShowOrderHistory(true);
  };

  const billingsTaxiTaxiToolsProps = {
    shouldShowOrderHistory,
    onOrderHistoryButtonClick,
  };

  return (
    <>
      <div className="billings">
        <HeadingComponent
          title={translations.header.headingText.replace("#{routeId}", routeId)}
        />
        <Row>
          <Column lg={8}>
            <Row>
              <Column withPaddings>
                <div className="billings_map_wrapper">
                  <MapComponent
                    markers={mapMarkers}
                    autoFit
                    autoFitOnUpdate
                    routes={mapRoutes}
                  />
                  <BillingsToolsComponent {...billingsTaxiTaxiToolsProps} />
                </div>
              </Column>
              <Column withPaddings>
                <ContractDetailsComponent
                  isLoading={billingsTaxiTaxiDetails.isLoading}
                  contractDetails={billingData?.contractDetails ?? null}
                />
              </Column>
              <Column withPaddings>
                <Row>
                  <Column lg={12}>
                    <BillingsRouteDetailsOverviewComponent
                      billingNodes={billingData?.billingNodes}
                    />
                  </Column>
                </Row>
              </Column>
              <Column withPaddings>
                <BillingRouteDetailsComponent billingData={billingData} />
              </Column>
              {!!billingData && (
                <Column withPaddings>
                  <TabsComponent data={tabsData} />
                </Column>
              )}
              <Column withPaddings>
                <BillingDetailsComponent billingData={billingData} />
              </Column>
              <Column>
                <div className="billings_submit_buttons_container">
                  {(userPermissions.hasAccessToAccept || isProducer()) &&
                    billingData?.status === BillingStatus.CREATED && (
                      <ButtonComponent
                        type="success"
                        onClick={() =>
                          onBillingStatusUpdate(ActionOption.ACCEPT)
                        }
                      >
                        {translations.acceptLabel}
                      </ButtonComponent>
                    )}

                  {(userPermissions.hasAccessToReject || isProducer()) &&
                    billingData?.status === BillingStatus.CREATED && (
                      <ButtonComponent
                        type="danger"
                        onClick={() =>
                          onBillingStatusUpdate(ActionOption.REJECT)
                        }
                      >
                        {translations.rejectLabel}
                      </ButtonComponent>
                    )}

                  {(userPermissions.hasAccessToReopen || isProducer()) &&
                    billingData?.status === BillingStatus.REOPEN_REQUEST && (
                      <ButtonComponent
                        type="primary"
                        onClick={() =>
                          onBillingStatusUpdate(ActionOption.REOPEN)
                        }
                      >
                        {translations.reopenLabel}
                      </ButtonComponent>
                    )}

                  {(userPermissions.hasAccessToRequestToReopen ||
                    isConsumer()) &&
                    billingData?.status === BillingStatus.ACCEPTED && (
                      <ButtonComponent
                        type="primary"
                        onClick={() =>
                          onBillingStatusUpdate(
                            ActionOption.SEND_REQUEST_TO_REOPEN,
                          )
                        }
                      >
                        {translations.sendRequestToReopenLabel}
                      </ButtonComponent>
                    )}
                </div>
              </Column>
              <Column withPaddings>
                <RelatedBillingsCargoTaxiComponent
                  forwardingId={billingData?.forwardingId!}
                />
              </Column>
              <Column withPaddings>
                <RelatedBillingsTaxiDriverComponent
                  planEntryId={billingData?.planEntryId}
                  forwardingId={billingData?.forwardingId!}
                />
              </Column>
              <Column withPaddings>
                <RelatedBillingsTaxiTaxiComponent
                  forwardingId={billingData?.forwardingId!}
                />
              </Column>
            </Row>
          </Column>
          <Column lg={4} withPaddings>
            <CardComponent
              classNames={{
                root: "billings_history_messenger_container",
                content: "h-100",
              }}
            >
              {!!billingData &&
                (shouldShowOrderHistory ? (
                  <OrderDetailsHistoryComponent
                    orderUuid={billingData.orderId}
                    refetchFlag={false}
                  />
                ) : (
                  <MessengerComponent
                    billingType={MessengerBillingType.TAXI}
                    channelsAvailability={messengerChannelsAvailability}
                    orderUuid={billingData.orderId}
                    billingUuid={billingUuid}
                    planEntryUuid={billingData.planEntryId}
                  />
                ))}
            </CardComponent>
          </Column>
        </Row>

        <BillingsTaxiTaxiModal
          isModalVisible={isModalVisible}
          onSubmit={(value) => onSubmitHandle(value)}
          onClose={onModalClose}
          actionOption={actionOption!}
        />
      </div>
    </>
  );
};

export default BillingsTaxiTaxiViewComponent;
